import { Enums } from "@/helpers/enums";
import { Links } from "@/helpers/links";
import { Ajax, MethodType, Web } from "@/helpers/web";
import { appData, C, D, J, Rnd, Text } from "@/helpers/global";

import { nextTick } from "vue";
import store from "@/store";
import router from '@/router';
import { UserToken } from "@/services/requests/user_token";
import { UserRefreshTokenLogin } from "@/services/requests/user_refreshTokenLogin";
import { StoreUser } from "@/store/constants";

let isRefreshing = false;
let pendingRequests = [];
let logoutHandler = null;

export const UserService = {
    SetLogoutHandler(handler) {
        logoutHandler = typeof handler === "function" ? handler : null;
    },
    IsLogin(callback) {
        const token = new UserToken(store.state.user?.token);
        if (!Text.IsEmpty(token?.accessToken)) {
            const j = appData.fn.parseJwt(token.accessToken)
            if (j.exp) {
                const now = Date.now();
                if (j.exp >= Math.floor(now / 1000) + 30) {
                    if (callback) { callback(); }
                    return true;
                }
            }
        }
        this.Logout();
        return false;
    },
    async GetTokenIsLogin(callback) {
        const token = await UserService.GetAccessRefreshToken(false);
        if (token) {
            if (callback) { await callback(); }
            return true;
        }
        else {
            this.Logout();
            return false;
        }
    },
    async GetAccessRefreshToken(escapeToken) {
        if (C.Bool(escapeToken)) {
            return null;
        }
        let token = new UserToken(store.getters[StoreUser.getToken]);
        if (!Text.IsEmpty(token?.accessToken)) {
            const j = appData.fn.parseJwt(token.accessToken)
            if (j.exp) {
                const now = Date.now();
                if (j.exp >= Math.floor(now / 1000) + 30) {
                    return token.accessToken;
                }
            }
        }
        const refreshToken = C.Str(token?.refreshToken);
        if (Text.IsEmpty(refreshToken)) {
            return null;
        }
        if (!isRefreshing) {
            isRefreshing = true;
            try {
                const newAccessToken = await new Promise((resolve, reject) => {
                    UserService.RefreshTokenLogin(new UserRefreshTokenLogin(refreshToken), (status) => {
                        if (status) {
                            token = new UserToken(store.getters[StoreUser.getToken]);
                            resolve(C.Str(token?.accessToken));
                        }
                        else {
                            reject("Failed to refresh token");
                        }
                    });
                });
                pendingRequests.forEach(callback => callback(newAccessToken));
                pendingRequests = [];
                return newAccessToken;
            }
            catch (ex) {
                console.warn(ex);
                return null;
            }
            finally {
                isRefreshing = false;
            }
        }
        else {
            // Yenileme işlemi devam ederken, istekleri beklet
            return new Promise((resolve) => {
                pendingRequests.push(resolve);
            });
        }
    },
    Logout(isAll) {
        let returnUrl = "";
        if (!isAll) {
            // Mevcut route bilgisini al
            const route = router.currentRoute.value;
            const isAllowReturn = (route.meta.theme == Enums.THEME.CLASSIC);
            if (isAllowReturn) {
                const url = new URL(window.location.href);
                returnUrl = `?returnUrl=${encodeURIComponent(`${url.pathname}${url.search}`)}`;
            }
        }

        try {
            const cleanupPromise = logoutHandler?.();

            if (cleanupPromise?.catch) {
                cleanupPromise.catch((error) => {
                    console.warn("Logout cleanup error:", error);
                });
            }
        }
        catch (error) {
            console.warn("Logout cleanup error:", error);
        }

        store.dispatch(StoreUser.logout, C.Bool(isAll));
        nextTick(() => {
            if (isAll) {
                Web.Redirect(Links.Url(Links.Login));
            }
            else {
                router.push(Links.Url(Links.Login) + returnUrl); // Router ile yönlendirme yapın
            }
        });
    },
    VerifyPhoneNumberRedirect() {
        let valid = J.Parse(localStorage.getItem("VerifyPhoneNumber"), "{}");
        const now = D.DateAdd(Enums.DATE.MINUTE, 5, D.ServerNow());
        if (!valid.endDate || (valid.endDate && D.DateTimeMinEqual(new Date(valid.endDate), now, true))) {
            valid = { token: Rnd.Text(25), endDate: D.DateAdd(Enums.DATE.MINUTE, 30, D.ServerNow()) };
            localStorage.setItem("VerifyPhoneNumber", J.String(valid));
        }
        Web.Redirect(Links.Url(Links.User.VerifyPhoneNumber) + '?token=' + valid.token);
    },
    LoginRedirect(returnUrl, phoneNumberConfirmed) {
        setTimeout(() => {
            if (C.Bool(phoneNumberConfirmed)) {
                if (returnUrl) {
                    Web.Redirect(decodeURIComponent(returnUrl));
                }
                else {
                    Web.Redirect(Links.Url(Links.Dashboard));
                }
            }
            else {
                UserService.VerifyPhoneNumberRedirect();
            }
        }, 100);
    },
    Signup(j, isRemember, callback, wait) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/signup", j, wait, null, true).then((data) => {
            store.dispatch(StoreUser.login, data);
            callback(data);
        });
    },
    Login(j, isRemember, callback, wait) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/login", j, wait, null, true).then((data) => {
            store.dispatch(StoreUser.login, data);
            callback(data);
        });
    },
    ExternalLogin(loginType, authToken, isRemember, callback, wait) {
        let endPoint = "";
        switch (loginType) {
            case Enums.LOGIN_TYPE.GOOGLE:
                endPoint = "/google-login";
                break;
            case Enums.LOGIN_TYPE.FACEBOOK:
                endPoint = "/facebook-login";
                break;
            case Enums.LOGIN_TYPE.FACEBOOK:
                endPoint = "/twitter-login";
                break;
            case Enums.LOGIN_TYPE.APPLE:
                endPoint = "/apple-login";
                break;
            default:
                if (wait) { wait(); }
                callback(false);
                return;
        }
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + endPoint, { "AuthToken": authToken }, wait, null, true).then((data) => {
            store.dispatch(StoreUser.login, data);
            callback(true, data);
        }).catch((err) => {
            if (!err) { err = {}; }
            callback(false, C.Str(C.Str(err.statusText, err.message), "There was a problem with the server! Please try again"));
        }, false);
    },
    RefreshTokenLogin(j, callback) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/refresh-token-login", j, null, null, true).then((data) => {
            store.dispatch(StoreUser.login, data);
            callback(true, data);
        }).catch((err) => {
            if (!err) { err = {}; }
            callback(false, C.Str(C.Str(err.statusText, err.message), "There was a problem with the server! Please try again"));
        }, false);
    },
    ForgotPassword(j, callback, wait) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/password-reset", j, wait, null, true).then((data) => {
            callback(data);
        });
    },
    VerifyResetToken(j, callback) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/verify-reset-token", j, null, null, true).then((data) => {
            callback(data);
        });
    },
    ResetPassword(j, callback, wait) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/reset-token-password-change", j, wait, null, true).then((data) => {
            callback(data);
        });
    },
    PhoneNumberVerificationCode(j, callback, wait) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/phone-number-verification-code", j, wait).then((data) => {
            callback(true, data);
        }).catch((err) => {
            if (!err) { err = {}; }
            callback(false, C.Str(C.Str(err.statusText, err.message), "There was a problem with the server! Please try again"));
        }, false);
    },
    VerifyPhoneNumberCode(j, callback, wait) {
        Ajax.Api(MethodType.Post, Enums.SERVICE.USER + "/verify-phone-number-code", j, wait).then((data) => {
            callback(data);
        }).catch((err) => {
            if (!err) { err = {}; }
            callback(false, C.Str(C.Str(err.statusText, err.message), "There was a problem with the server! Please try again"));
        }, false);
    },
    Me(callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.USER + "/me", null, wait).then((data) => {
            callback(data);
        });
    },
    MeDetail(callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.USER + "/me-detail", null, wait).then((data) => {
            callback(data);
        });
    },
    MeDashboard(callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.USER + "/me-dashboard", null, wait).then((data) => {
            callback(data);
        });
    },
    StudentDashboard(studentId, callback, wait) {
        Ajax.Api(MethodType.Get, Enums.SERVICE.USER + "/student-dashboard/" + studentId, null, wait).then((data) => {
            callback(data);
        });
    },
    ProfileChange(j, callback, wait) {
        Ajax.Api(MethodType.Put, Enums.SERVICE.USER + "/profile-change", j, wait).then((data) => {
            const user = store.getters[StoreUser.get];
            user.fullName = j.name + " " + j.lastName;
            if (C.Str(data) != "") {
                user.profile = data;
            }
            store.dispatch(StoreUser.set, user);
            callback(data);
        });
    },
    DetailChange(j, callback, wait) {
        Ajax.Api(MethodType.Put, Enums.SERVICE.USER + "/detail-change", j, wait).then((data) => {
            callback(data);
        });
    },
    UserTypePackageChange(j, callback, wait) {
        Ajax.Api(MethodType.Put, Enums.SERVICE.USER + "/user-type-package-change", j, wait).then((data) => {
            store.dispatch(StoreUser.login, data);
            callback(data);
        });
    },
    ObpRecognitionChange(j, callback, wait) {
        Ajax.Api(MethodType.Put, Enums.SERVICE.USER + "/obp-recognition-change", j, wait).then((data) => {
            callback(data);
        });
    },
    TeacherPaymentInfoChange(j, callback, wait) {
        Ajax.Api(MethodType.Put, Enums.SERVICE.USER + "/teacher-payment-info-change", j, wait).then((data) => {
            callback(data);
        });
    },
};